/**
 * Cross-tab exclusivity for the refresh call.
 *
 * Refresh tokens ROTATE: `extend-session` consumes the one it is given and mints a new
 * pair. Two tabs refreshing at once means the second call arrives with a token the first
 * already spent, gets a 401, and — if that were read as "session over" — tears down the
 * session the first tab had just renewed. So exactly one tab holds the lease while it
 * refreshes; the others wait, then re-read storage and adopt what the winner wrote.
 *
 * `navigator.locks` is the real primitive (held across tabs, released on tab death). The
 * fallback is a localStorage lease with a TTL — coarser, but a crashed holder is only ever
 * a TTL away from release, and the jitter makes a same-millisecond race unlikely.
 */

const LOCK_NAME = 'ielect:session-refresh';
const LEASE_KEY = 'ielect.session-refresh-lease';
const LEASE_TTL_MS = 15_000;
const MAX_JITTER_MS = 500;
const POLL_MS = 100;

interface ILease {
	owner: string;
	expiresAt: number;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const readLease = (): ILease | null => {
	try {
		const raw = localStorage.getItem(LEASE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<ILease>;
		return typeof parsed.owner === 'string' && typeof parsed.expiresAt === 'number' ? (parsed as ILease) : null;
	} catch {
		return null;
	}
};

const writeLease = (lease: ILease | null) => {
	try {
		if (lease) localStorage.setItem(LEASE_KEY, JSON.stringify(lease));
		else localStorage.removeItem(LEASE_KEY);
	} catch {
		// Storage unavailable — fall through; the caller still runs, just without exclusivity.
	}
};

const withStorageLease = async <T>(fn: () => Promise<T>): Promise<T> => {
	const owner = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
	await sleep(Math.floor(Math.random() * MAX_JITTER_MS));

	const deadline = Date.now() + LEASE_TTL_MS * 2;
	while (Date.now() < deadline) {
		const current = readLease();
		if (!current || current.expiresAt <= Date.now()) {
			writeLease({ owner, expiresAt: Date.now() + LEASE_TTL_MS });
			// Re-read: two tabs can pass the "free" check in the same tick.
			if (readLease()?.owner === owner) break;
		}
		await sleep(POLL_MS);
	}

	try {
		return await fn();
	} finally {
		if (readLease()?.owner === owner) writeLease(null);
	}
};

interface ILockManagerLike {
	request: <T>(name: string, cb: () => Promise<T>) => Promise<T>;
}

const webLocks = (): ILockManagerLike | null => {
	if (typeof navigator === 'undefined') return null;
	const locks = (navigator as Navigator & { locks?: ILockManagerLike }).locks;
	return locks && typeof locks.request === 'function' ? locks : null;
};

/** Run `fn` while holding the cross-tab refresh lease. */
export const withRefreshLease = <T>(fn: () => Promise<T>): Promise<T> => {
	const locks = webLocks();
	return locks ? locks.request(LOCK_NAME, fn) : withStorageLease(fn);
};

/** Test seam only. */
export const __leaseInternalsForTests = { LEASE_KEY, LEASE_TTL_MS };
